// Per-app totals and shares for process, case, and step scopes.
import { INSIGNIFICANT_COLOR, getNodeColor } from "../analytics/palette";
import type { AppUsageRow } from "./AppUsageBars";
import { appIconUri } from "./appIcon";
import type { ProcessGraphNode, ProcessInstance } from "./processGraphModel";

// Intermediate slice before folding into rows (no color/icon resolved yet).
type AppSlice = { name: string; seconds: number; pct: number };

// Apps beyond this rank — and any that round to 0% — collapse into "Other apps", which also
// absorbs the in-scope time no app attribute accounts for. Keeps the narrow panel legible.
const APP_USAGE_TOP_N = 6;

// Sort apps by share, keep the top N significant ones, and fold everyone else — plus the
// unattributed `otherBase` — into a single "Other apps" row. Only rows reaching ~0.5% survive
// (anything less rounds to "0%").
const foldAppRows = (
  apps: AppSlice[],
  otherBase: { seconds: number; pct: number },
  otherLabel: string,
  maxApps = APP_USAGE_TOP_N,
): AppUsageRow[] => {
  const sorted = [...apps].sort((a, b) => b.pct - a.pct);
  const kept: AppSlice[] = [];
  let otherSeconds = otherBase.seconds;
  let otherPct = otherBase.pct;
  for (const app of sorted) {
    if (kept.length < maxApps && app.pct >= 0.5) {
      kept.push(app);
    } else {
      otherSeconds += app.seconds;
      otherPct += app.pct;
    }
  }
  const rows: AppUsageRow[] = kept.map(app => ({
    name: app.name,
    seconds: app.seconds,
    pct: app.pct,
    color: getNodeColor(app.name),
    iconUri: appIconUri(app.name),
  }));
  if (otherPct >= 0.5) {
    rows.push({
      name: otherLabel,
      seconds: otherSeconds,
      pct: otherPct,
      color: INSIGNIFICANT_COLOR,
      iconUri: null,
      isOther: true,
    });
  }
  return rows;
};

// Pooled (duration-weighted) app usage across a flat list of steps — used for the whole-process
// case and single-step scopes. Shares are Σ appSeconds ÷ total, and "Other apps" is whatever the
// total duration isn't attributed to. The denominator is clamped up to the attributed sum so
// overlapping in-step app time can never push the bars past 100%.
export const buildPooledAppRows = (
  steps: { duration: number; appSeconds?: Record<string, number> }[],
  otherLabel: string,
  maxApps = APP_USAGE_TOP_N,
): AppUsageRow[] => {
  let totalDuration = 0;
  let attributed = 0;
  const secondsByApp = new Map<string, number>();
  for (const step of steps) {
    totalDuration += step.duration;
    for (const [app, seconds] of Object.entries(step.appSeconds ?? {})) {
      secondsByApp.set(app, (secondsByApp.get(app) ?? 0) + seconds);
      attributed += seconds;
    }
  }
  const denom = Math.max(totalDuration, attributed);
  if (denom <= 0) return [];
  const apps: AppSlice[] = [...secondsByApp.entries()].map(([name, seconds]) => ({
    name,
    seconds,
    pct: (seconds / denom) * 100,
  }));
  const other = {
    seconds: denom - attributed,
    pct: ((denom - attributed) / denom) * 100,
  };
  return foldAppRows(apps, other, otherLabel, maxApps);
};

// An app belongs to a step when it holds at least this share of the step's attributed time; below
// that it is a glance (a notification, a quick lookup), not the way the step is done.
const STEP_APP_MIN_SHARE = 0.1;

/** The apps a step is worked in, most-used first; the step's dominant app when unattributed. */
export const stepApps = (node: ProcessGraphNode): string[] => {
  const secondsByApp = new Map<string, number>();
  let attributed = 0;
  for (const occurrence of node.instances) {
    for (const [app, seconds] of Object.entries(occurrence.appSeconds ?? {})) {
      if (seconds <= 0) continue;
      secondsByApp.set(app, (secondsByApp.get(app) ?? 0) + seconds);
      attributed += seconds;
    }
  }
  const apps = [...secondsByApp]
    .filter(([, seconds]) => seconds / attributed >= STEP_APP_MIN_SHARE)
    .sort((a, b) => b[1] - a[1])
    .map(([app]) => app);
  return apps.length > 0 ? apps : node.app ? [node.app] : [];
};

export type AppTableRow = {
  app: string;
  seconds: number;
  /** Share of all app-attributed time in scope. */
  share: number;
  /** Cases in scope with any time in the app. */
  cases: number;
  /** Steps the app is part of (see stepApps). */
  stepIds: string[];
};

/**
 * Time per app across the cases in scope, for the Apps tab's table. Apps under half a percent of
 * the attributed time are grouped into `other`, retaining their rows for the expandable list.
 */
export const appUsageTable = (
  instances: ProcessInstance[],
  steps: ProcessGraphNode[],
): {
  rows: AppTableRow[];
  other: { rows: AppTableRow[]; seconds: number };
  totalSeconds: number;
} => {
  const secondsByApp = new Map<string, number>();
  const casesByApp = new Map<string, number>();
  for (const instance of instances) {
    const used = new Set<string>();
    for (const step of instance.steps) {
      for (const [app, seconds] of Object.entries(step.appSeconds ?? {})) {
        if (seconds <= 0) continue;
        secondsByApp.set(app, (secondsByApp.get(app) ?? 0) + seconds);
        used.add(app);
      }
    }
    for (const app of used) casesByApp.set(app, (casesByApp.get(app) ?? 0) + 1);
  }
  const totalSeconds = [...secondsByApp.values()].reduce((sum, seconds) => sum + seconds, 0);
  const stepIdsByApp = new Map<string, string[]>();
  for (const node of steps) {
    for (const app of stepApps(node))
      stepIdsByApp.set(app, [...(stepIdsByApp.get(app) ?? []), node.id]);
  }
  const rows: AppTableRow[] = [];
  const other = { rows: [] as AppTableRow[], seconds: 0 };
  for (const [app, seconds] of [...secondsByApp].sort((a, b) => b[1] - a[1])) {
    const share = totalSeconds > 0 ? seconds / totalSeconds : 0;
    const row = {
      app,
      seconds,
      share,
      cases: casesByApp.get(app) ?? 0,
      stepIds: stepIdsByApp.get(app) ?? [],
    };
    if (share * 100 < 0.5) {
      other.rows.push(row);
      other.seconds += seconds;
    } else {
      rows.push(row);
    }
  }
  return { rows, other, totalSeconds };
};
